import React, { useState, useEffect } from 'react';
import { Server, Activity, Users, HardDrive, Wifi, Clock, TrendingUp, X } from 'lucide-react';
import Sidebar from '../Components/Sidebar';

const Remotestats = () => {
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiKey = 'ghfvyu6778jvfts54w675786fghd';
  const baseUrl = 'https://admin-relay.checkscript.site';

  const fetchData = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      };

      const [statsRes, connectionsRes] = await Promise.all([
        fetch(`${baseUrl}/api/stats`, { headers }),
        fetch(`${baseUrl}/api/connections`, { headers })
      ]);

      if (!statsRes.ok || !connectionsRes.ok) {
        throw new Error('API request failed');
      }

      const statsData = await statsRes.json();
      const connectionsData = await connectionsRes.json();

      setStats(statsData.data);
      setConnections(connectionsData.data.connections);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const disconnectHost = async (deviceId) => {
    try {
      const res = await fetch(`${baseUrl}/api/disconnect/${deviceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      alert('Failed to disconnect: ' + err.message);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-white text-xl">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-900">
      <Sidebar />
      
      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Remote Desktop Server</h1>
                <p className="text-gray-400">Real-time monitoring and management</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400">Uptime</div>
                <div className="text-2xl font-bold text-green-400">{stats?.uptimeFormatted || '0h 0m'}</div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={<Wifi className="w-6 h-6" />}
              label="Active Connections"
              value={stats?.activeConnections || 0}
              color="blue"
              subtitle={`${stats?.activeClients || 0} with clients`}
            />
            <StatCard
              icon={<Users className="w-6 h-6" />}
              label="Total Connections"
              value={stats?.totalConnectionsEver || 0}
              color="purple"
              subtitle={`Peak: ${stats?.peakConnections || 0}`}
            />
            <StatCard
              icon={<Activity className="w-6 h-6" />}
              label="Frames Processed"
              value={formatNumber(stats?.totalFramesProcessed || 0)}
              color="green"
              subtitle={`${((stats?.totalFramesProcessed / (stats?.uptime / 1000)) || 0).toFixed(1)} FPS avg`}
            />
            <StatCard
              icon={<HardDrive className="w-6 h-6" />}
              label="Data Transferred"
              value={`${stats?.totalBytesTransferredGB || 0} GB`}
              color="yellow"
              subtitle={`Heap: ${stats?.memory?.heapUsedMB || 0}MB`}
            />
          </div>

          {/* Active Connections Table */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Server className="w-5 h-5" />
                Active Connections ({connections.length})
              </h2>
            </div>
            
            {connections.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-400">
                No active connections
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Device ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Connected</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Idle Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Frames</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">FPS</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {connections.map((conn) => (
                      <tr key={conn.deviceId} className="hover:bg-gray-750 transition-colors">
                        <td className="px-4 py-3">
                          <div className="text-sm font-mono text-white">{conn.deviceId}</div>
                          <div className="text-xs text-gray-400">{conn.ip}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${conn.hasClient ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                            <span className="text-sm text-white">
                              {conn.hasClient ? 'With Client' : 'Waiting'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-white">{conn.connectedDurationFormatted}</div>
                          <div className="text-xs text-gray-400">{new Date(conn.connectedAt).toLocaleTimeString()}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`text-sm ${conn.idleTime > 600000 ? 'text-yellow-400' : 'text-white'}`}>
                            {conn.idleTimeFormatted}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-white">
                          {formatNumber(conn.frameCount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-white">
                          {conn.bytesTransferredMB} MB
                        </td>
                        <td className="px-4 py-3 text-sm text-white">
                          {typeof conn.averageFPS === 'number' ? conn.averageFPS.toFixed(1) : '0.0'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => disconnectHost(conn.deviceId)}
                            className="p-1 hover:bg-red-600 rounded text-red-400 hover:text-white transition-colors"
                            title="Force Disconnect"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            Auto-refreshing every 5 seconds • Last update: {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color, subtitle }) => {
  const colorClasses = {
    blue: 'from-blue-600 to-blue-700',
    purple: 'from-purple-600 to-purple-700',
    green: 'from-green-600 to-green-700',
    yellow: 'from-yellow-600 to-yellow-700'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-6 shadow-lg`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-white opacity-80">{icon}</div>
      </div>
      <div className="text-3xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-white opacity-90 mb-1">{label}</div>
      {subtitle && <div className="text-xs text-white opacity-70">{subtitle}</div>}
    </div>
  );
};

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

export default Remotestats;